import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { observer } from '@legendapp/state/react';
import { useRouter } from 'expo-router';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SettingsAdvancedLogger } from '../../../../utils/logger';
import theme from '../../../../config/theme';
import { APP_CONSTANTS } from '../../../../config/constants';
import {
  getSettingsContentPaddingBottom,
  getSettingsHeaderPaddingTop,
} from '@/utils/safeArea';
import { IS_CHECKLIST_RECOVERY_DIAGNOSTIC } from '@/config/recoveryDiagnostic';
import {
  findPendingChecklistRecoveryExport,
  generateAndUploadChecklistRecoveryDiagnostic,
  type RecoveryUploadReceipt,
} from '@/services/recovery/checklistRecoveryExport';

// Logger para a página de configurações avançadas
const logger = SettingsAdvancedLogger.Main;

// Interface para o componente de item de informação
interface InfoItemProps {
  label: string;
  value: string;
}

// Componente para exibir informações
const InfoItem = ({ label, value }: InfoItemProps) => {
  return (
    <View style={styles.infoItem}>
      <Text style={styles.infoLabel}>{label}</Text>
      <Text style={styles.infoValue}>{value}</Text>
    </View>
  );
};

// Interface para o componente de botão de ação
interface ActionButtonProps {
  icon: string;
  label: string;
  onPress: () => void;
  disabled?: boolean;
}

// Mapeamento de ícones para MaterialCommunityIcons
const iconMap: Record<string, string> = {
  'arrow-back': 'arrow-left',
  'download-outline': 'download-outline',
  'cloud-upload-outline': 'cloud-upload-outline',
  'check-circle-outline': 'check-circle-outline',
  'sync': 'sync',
  'trash-can-outline': 'trash-can-outline',
};

// Componente para botões de ação
const ActionButton = ({ icon, label, onPress, disabled = false }: ActionButtonProps) => {
  return (
    <TouchableOpacity
      style={[styles.actionButton, disabled && styles.actionButtonDisabled]}
      onPress={onPress}
      disabled={disabled}
    >
      <MaterialCommunityIcons name={iconMap[icon] as any} size={22} color={theme.colors.primary.default} />
      <Text style={styles.actionButtonLabel}>{label}</Text>
    </TouchableOpacity>
  );
};

// Página principal de configurações avançadas
const AdvancedScreen = observer(() => {
  const router = useRouter();
  const safeAreaInsets = useSafeAreaInsets();
  const headerPaddingTop = getSettingsHeaderPaddingTop(safeAreaInsets.top);
  const scrollViewBottomPadding = getSettingsContentPaddingBottom(safeAreaInsets.bottom);
  const [isExporting, setIsExporting] = useState(false);
  const [hasPendingRecoveryUpload, setHasPendingRecoveryUpload] = useState(false);
  const [recoveryReceipt, setRecoveryReceipt] = useState<RecoveryUploadReceipt | null>(null);
  const systemVersionLabel =
    typeof Platform.Version === 'string' || typeof Platform.Version === 'number'
      ? String(Platform.Version)
      : Platform.OS === 'web'
        ? 'Navegador'
        : 'Não disponível';

  useEffect(() => {
    logger.debug('Página de configurações avançadas inicializada');
    let cancelled = false;
    if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
      void findPendingChecklistRecoveryExport()
        .then(pending => {
          if (!cancelled) setHasPendingRecoveryUpload(Boolean(pending));
        })
        .catch(() => undefined);
    }
    return () => {
      cancelled = true;
      logger.debug('Página de configurações avançadas desmontada');
    };
  }, []);

  // Funções para manipular as ações
  const performRecoveryExport = async () => {
    setIsExporting(true);
    setRecoveryReceipt(null);

    try {
      const result = await generateAndUploadChecklistRecoveryDiagnostic();
      setRecoveryReceipt(result);
      setHasPendingRecoveryUpload(false);
      Alert.alert(
        'Diagnóstico enviado com sucesso',
        `Identificador ${result.identifier}. Agora instale a versão normal 1.0.71 por cima, sem desinstalar nem limpar os dados.`,
        [{ text: 'OK' }]
      );
    } catch (error) {
      console.error('[RECOVERY] upload:failed');
      const pending = await findPendingChecklistRecoveryExport().catch(() => null);
      setHasPendingRecoveryUpload(Boolean(pending));
      Alert.alert(
        'Não foi possível enviar',
        `${error instanceof Error ? error.message : 'Falha inesperada no envio.'} O pacote gerado foi mantido neste aparelho. Conecte-se à internet e tente novamente.`,
        [{ text: 'OK' }]
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportData = () => {
    Alert.alert(
      hasPendingRecoveryUpload ? 'Tentar enviar novamente?' : 'Gerar e enviar diagnóstico?',
      hasPendingRecoveryUpload
        ? 'O pacote criptografado que já está neste aparelho será reenviado.'
        : 'Os dados locais desta conta serão criptografados e enviados ao suporte autorizado.',
      [
        { text: 'Cancelar', style: 'cancel' },
        { text: hasPendingRecoveryUpload ? 'Tentar novamente' : 'Gerar e enviar', onPress: () => void performRecoveryExport() },
      ]
    );
  };

  const handleLegacyExportData = () => {
    setIsExporting(true);
    logger.debug('Iniciando exportação de dados');

    setTimeout(() => {
      setIsExporting(false);
      Alert.alert(
        'Exportação concluída',
        'Os dados foram exportados com sucesso. Você pode encontrar o arquivo na pasta Downloads.',
        [{ text: 'OK' }]
      );
    }, 2000);
  };

  // Navegação para voltar
  const handleGoBack = () => {
    if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
      return;
    }
    router.back();
  };

  return (
    <View style={styles.container}>
      <View style={[styles.header, { paddingTop: headerPaddingTop }]}>
        {IS_CHECKLIST_RECOVERY_DIAGNOSTIC ? (
          <View style={styles.backButton} />
        ) : (
          <TouchableOpacity
            style={styles.backButton}
            onPress={handleGoBack}
          >
            <MaterialCommunityIcons name="arrow-left" size={24} color={theme.colors.text.inverse} />
          </TouchableOpacity>
        )}
        <Text style={styles.headerTitle}>Avançado</Text>
        <View style={styles.headerRight} />
      </View>

      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollViewContent, { paddingBottom: scrollViewBottomPadding }]}
        showsVerticalScrollIndicator={false}
      >
        {/* Seção de informações do aplicativo */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Informações do aplicativo</Text>
          
          <InfoItem
            label="Versão do aplicativo"
            value={APP_CONSTANTS.VERSION}
          />
          
          <InfoItem
            label="Plataforma"
            value={Platform.OS === 'ios' ? 'iOS' : Platform.OS === 'android' ? 'Android' : 'Web'}
          />

          <InfoItem
            label="Versão do sistema"
            value={systemVersionLabel}
          />
        </View>

        {IS_CHECKLIST_RECOVERY_DIAGNOSTIC ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Recuperação de checklists</Text>
            <Text style={styles.recoveryNotice}>
              Este aplicativo está em modo seguro. A sincronização permanece bloqueada durante a coleta.
            </Text>

            <View style={styles.actionsContainer}>
              <ActionButton
                icon="cloud-upload-outline"
                label={hasPendingRecoveryUpload ? 'Tentar enviar diagnóstico novamente' : 'Gerar e enviar diagnóstico'}
                onPress={handleExportData}
                disabled={isExporting}
              />
            </View>

            {recoveryReceipt && (
              <View style={styles.recoveryResult}>
                <View style={styles.recoverySuccessTitle}>
                  <MaterialCommunityIcons name="check-circle-outline" size={22} color={theme.colors.util.success} />
                  <Text style={styles.recoveryResultTitle}>Diagnóstico enviado com sucesso</Text>
                </View>
                <Text style={styles.recoveryIdentifier}>Identificador: {recoveryReceipt.identifier}</Text>
                <Text style={styles.recoverySummary}>Instale agora a versão normal 1.0.71 por cima deste aplicativo, sem desinstalar nem limpar os dados.</Text>
              </View>
            )}
          </View>
        ) : (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Gerenciamento de dados</Text>

            <View style={styles.actionsContainer}>
              <ActionButton
                icon="download-outline"
                label="Exportar dados (Excel)"
                onPress={handleLegacyExportData}
                disabled={isExporting}
              />

              <ActionButton
                icon="sync"
                label="Sincronizar dados"
                onPress={() => Alert.alert('Info', 'Sincronização iniciada.')}
              />

              <ActionButton
                icon="trash-can-outline"
                label="Limpar cache"
                onPress={() => Alert.alert('Info', 'Cache limpo com sucesso.')}
              />
            </View>
          </View>
        )}



        {/* Exibir indicador durante exportação */}
        {isExporting && (
          <View style={styles.exportingContainer}>
            <ActivityIndicator size="small" color={theme.colors.primary.default} />
            <Text style={styles.exportingText}>
              {IS_CHECKLIST_RECOVERY_DIAGNOSTIC ? 'Preparando e enviando diagnóstico...' : 'Exportando dados...'}
            </Text>
          </View>
        )}
      </ScrollView>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  header: {
    backgroundColor: theme.colors.primary.default,
    padding: theme.spacing.lg,
    paddingTop: Platform.OS === 'ios' ? theme.spacing.xl + theme.spacing.sm : theme.spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: theme.fontSizes.xl,
    fontWeight: theme.fontWeights.bold as any,
    color: theme.colors.text.inverse,
  },
  headerRight: {
    width: 40,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    paddingBottom: theme.spacing.xl,
  },
  section: {
    backgroundColor: theme.colors.background.primary,
    margin: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.lg,
    ...theme.shadows.md,
  },
  sectionTitle: {
    fontSize: theme.fontSizes.lg,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.md,
  },
  infoItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: theme.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border.light,
  },
  infoLabel: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.secondary,
  },
  infoValue: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
    color: theme.colors.text.primary,
  },
  actionsContainer: {
    marginTop: theme.spacing.sm,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border.light,
  },
  actionButtonDisabled: {
    opacity: 0.5,
  },
  actionButtonLabel: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
    marginLeft: theme.spacing.md,
  },
  exportingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.md,
    margin: theme.spacing.md,
    ...theme.shadows.sm,
  },
  exportingText: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
    marginLeft: theme.spacing.md,
  },
  recoveryNotice: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.md,
    lineHeight: 22,
  },
  recoveryResult: {
    marginTop: theme.spacing.lg,
    paddingTop: theme.spacing.md,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border.light,
  },
  recoveryResultTitle: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
    marginLeft: theme.spacing.sm,
  },
  recoverySuccessTitle: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  recoveryIdentifier: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.semibold as any,
    marginTop: theme.spacing.md,
  },
  recoverySummary: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
    marginTop: theme.spacing.md,
  },
});

export default AdvancedScreen;
